import api, { handleApiResponse, handleApiError } from './api';

// Configuration constants
const CONFIG = {
  DEFAULT_TIMEOUT: 30000,
  CACHE_TTL: 300000, // 5 minutes
  MAX_DATE_RANGE_DAYS: 365,
  MIN_DATE_RANGE_DAYS: 1,
  MAX_LIMIT: 100,
  MIN_LIMIT: 1,
  VALID_FORMATS: ['pdf', 'csv'],
  VALID_STAFF_LEVELS: [1, 2, 3]
};

// Custom error classes for better error handling
class StaffReportError extends Error {
  constructor(message, code, context = {}) {
    super(message);
    this.name = 'StaffReportError';
    this.code = code;
    this.context = context;
  }
}

class ValidationError extends StaffReportError {
  constructor(message, errors = {}) {
    super(message, 'VALIDATION_ERROR', { errors });
  }
}

// Utility class for parameter handling
class ParameterUtils {
  static cleanParams(params) {
    const cleanParams = { ...params };
    
    // Handle boolean conversion for is_active
    if (cleanParams.is_active !== undefined && cleanParams.is_active !== '') {
      cleanParams.is_active = cleanParams.is_active === true || cleanParams.is_active === 'true' ? 'true' : 'false';
    }
    
    // Handle include_details boolean
    if (cleanParams.include_details !== undefined) {
      cleanParams.include_details = cleanParams.include_details ? 'true' : 'false';
    }
    
    // Remove empty/null/undefined values
    Object.keys(cleanParams).forEach(key => {
      if (cleanParams[key] === '' || cleanParams[key] === null || cleanParams[key] === undefined) {
        delete cleanParams[key];
      }
    });
    
    return cleanParams;
  }

  static validateStaffId(staffId) {
    if (!staffId || isNaN(Number(staffId)) || Number(staffId) <= 0) {
      throw new ValidationError('Valid staff ID is required', { staffId: 'Must be a positive number' });
    }
    return Number(staffId);
  }
}

// File download utility
class FileDownloadManager {
  static handleFileDownload(response, defaultFilename) {
    try {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = defaultFilename;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true, filename };
    } catch (error) {
      throw new StaffReportError('File download failed', 'DOWNLOAD_ERROR', { error: error.message });
    }
  }
}

// Cache manager for performance optimization
class CacheManager {
  constructor() {
    this._cache = new Map();
    this._cacheExpiry = new Map();
  }
  
  get(key, ttlMs = CONFIG.CACHE_TTL) {
    const expired = Date.now() > (this._cacheExpiry.get(key) || 0);
    if (expired) {
      this._cache.delete(key);
      this._cacheExpiry.delete(key);
      return null;
    }
    return this._cache.get(key);
  }
  
  set(key, value, ttlMs = CONFIG.CACHE_TTL) {
    this._cache.set(key, value);
    this._cacheExpiry.set(key, Date.now() + ttlMs);
  }
  
  clear() {
    this._cache.clear();
    this._cacheExpiry.clear();
  }
}

// Validator class for comprehensive validation
class StaffReportValidator {
  static validateReportParams(params) {
    const errors = {};
    const warnings = [];

    // Date validation
    if (params.date_from && params.date_to) {
      const fromDate = new Date(params.date_from);
      const toDate = new Date(params.date_to);

      if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
        errors.dateFormat = 'Invalid date format. Use YYYY-MM-DD';
      } else {
        if (fromDate > toDate) {
          errors.dateRange = 'From date must be before to date';
        }

        if (toDate > new Date()) {
          errors.dateTo = 'To date cannot be in the future';
        }

        const daysDiff = (toDate - fromDate) / (1000 * 60 * 60 * 24);
        if (daysDiff > CONFIG.MAX_DATE_RANGE_DAYS) {
          errors.dateRange = `Date range cannot exceed ${CONFIG.MAX_DATE_RANGE_DAYS} days`;
        }

        if (daysDiff < CONFIG.MIN_DATE_RANGE_DAYS) {
          errors.dateRange = `Date range must be at least ${CONFIG.MIN_DATE_RANGE_DAYS} day`;
        }
      }
    }

    // Limit validation
    if (params.limit !== undefined) {
      const limit = Number(params.limit);
      if (isNaN(limit) || limit < CONFIG.MIN_LIMIT || limit > CONFIG.MAX_LIMIT) {
        errors.limit = `Limit must be between ${CONFIG.MIN_LIMIT} and ${CONFIG.MAX_LIMIT}`;
      }
    }

    // Staff level validation
    if (params.staff_level !== undefined && params.staff_level !== '') {
      const level = Number(params.staff_level);
      if (isNaN(level) || !CONFIG.VALID_STAFF_LEVELS.includes(level)) {
        errors.staff_level = `Invalid staff level. Must be one of: ${CONFIG.VALID_STAFF_LEVELS.join(', ')}`;
      }
    }

    // Format validation
    if (params.format && !CONFIG.VALID_FORMATS.includes(params.format)) {
      errors.format = `Invalid export format. Must be one of: ${CONFIG.VALID_FORMATS.join(', ')}`;
    }

    // Category validation
    if (params.category_id !== undefined && params.category_id !== '') {
      const categoryId = Number(params.category_id);
      if (isNaN(categoryId) || categoryId <= 0) {
        errors.category_id = 'Category ID must be a positive number';
      }
    }

    // Performance warnings
    if (params.include_details === 'true' && !params.date_from) {
      warnings.push('Including details without date range may impact performance');
    }

    if (params.limit && Number(params.limit) > 50 && params.include_details === 'true') {
      warnings.push('High limit with detailed data may cause slow response times');
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
      warnings
    };
  }
}

// Data formatter class for clean separation
class StaffReportFormatter {
  static formatReportData(reportData) {
    if (!reportData?.data) {
      console.warn('Invalid report data structure received');
      return null;
    }

    const data = reportData.data;
    
    if (!data.staff_info) {
      throw new StaffReportError('Missing staff_info in report data', 'INVALID_DATA');
    }

    return {
      staffInfo: {
        ...data.staff_info,
        expertise: Array.isArray(data.staff_info.expertise) ? data.staff_info.expertise : [],
        expertiseSummary: data.staff_info.expertise_summary || {
          total_categories: 0,
          can_receive_forwarded: 0,
          max_capacity: 0,
          categories_list: []
        }
      },
      reportPeriod: data.report_period || {},
      ticketStatistics: data.ticket_statistics || {},
      actionStatistics: data.action_statistics || {},
      assignmentStatistics: data.assignment_statistics || {},
      performanceMetrics: data.performance_metrics || {},
      categoryBreakdown: Array.isArray(data.category_breakdown) ? data.category_breakdown : [],
      detailedData: data.detailed_data || null,
      reportMetadata: data.report_metadata || {}
    };
  }

  static formatStaffList(staffListData) {
    if (!staffListData?.data?.staff) {
      console.warn('Invalid staff list data structure');
      return [];
    }

    return staffListData.data.staff.map(staff => ({
      value: staff.id,
      label: staff.display_name || `${staff.name} (${staff.email})`,
      name: staff.name,
      email: staff.email,
      staffLevel: staff.staff_level,
      staffLevelName: staff.staff_level_name,
      userIdNumber: staff.user_id_number,
      isActive: Boolean(staff.is_active)
    }));
  }

  static formatSearchResults(searchData) {
    if (!searchData?.data?.staff) {
      console.warn('Invalid search results data structure');
      return [];
    }

    return searchData.data.staff.map(staff => ({
      id: staff.id,
      name: staff.name,
      email: staff.email,
      userIdNumber: staff.user_id_number,
      staffLevel: staff.staff_level,
      staffLevelName: staff.staff_level_name,
      isActive: Boolean(staff.is_active),
      lastLoginAt: staff.last_login_at,
      createdAt: staff.created_at,
      expertise: Array.isArray(staff.expertise) ? staff.expertise : [],
      expertiseSummary: staff.expertise_summary || {},
      enhancedStats: {
        ...staff.enhanced_stats,
        lifecycle_metrics: {
          ...staff.enhanced_stats?.lifecycle_metrics,
          closed: staff.enhanced_stats?.lifecycle_metrics?.closed || 0
        }
      } || {},
      dateRange: staff.date_range || {}
    }));
  }

  static formatSummaryData(summaryData) {
    if (!summaryData?.data) {
      console.warn('Invalid summary data structure');
      return null;
    }

    const data = summaryData.data;
    return {
      period: data.period || {},
      overview: data.overview || {},
      staffBreakdown: data.staff_breakdown || {},
      topPerformers: Array.isArray(data.top_performers) ? data.top_performers : [],
      systemHealth: data.system_health || {},
      generatedBy: data.generated_by || {}
    };
  }

  static formatDuration(minutes) {
    if (!minutes || minutes === 0) return '0 min';

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.round(minutes % 60);

    if (hours === 0) {
      return `${remainingMinutes} min`;
    } else if (remainingMinutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${remainingMinutes}m`;
    }
  }

  static formatPercentage(value, decimals = 1) {
    if (value === null || value === undefined || isNaN(value)) return '0.0%';
    return `${Number(value).toFixed(decimals)}%`;
  }

  static formatNumber(value, decimals = 0) {
    if (value === null || value === undefined || isNaN(value)) return '0';
    if (decimals > 0) {
      return Number(value).toFixed(decimals);
    }
    return new Intl.NumberFormat().format(value);
  }
}

// Main service class
class StaffReportService {
  constructor() {
    this._cache = new CacheManager();
    this._dateRangePresets = null;
  }

  // Core API methods - FIXED ENDPOINTS TO MATCH LARAVEL ROUTES
  async searchStaff(params = {}) {
    try {
      const validation = StaffReportValidator.validateReportParams(params);
      if (!validation.isValid) {
        throw new ValidationError('Invalid search parameters', validation.errors);
      }

      const cleanParams = ParameterUtils.cleanParams(params);
      const cacheKey = `search:${JSON.stringify(cleanParams)}`;
      
      // Check cache for non-real-time data
      if (!cleanParams.date_from && !cleanParams.date_to) {
        const cached = this._cache.get(cacheKey);
        if (cached) return cached;
      }

      // FIXED: Use the exact endpoint from Laravel routes
      const response = await api.get('/staff/reports/search', { 
        params: cleanParams,
        timeout: CONFIG.DEFAULT_TIMEOUT
      });
      
      const result = handleApiResponse(response);
      const formattedResult = {
        ...result,
        data: {
          ...result.data,
          staff: StaffReportFormatter.formatSearchResults(result)
        }
      };

      // Cache if not date-filtered
      if (!cleanParams.date_from && !cleanParams.date_to) {
        this._cache.set(cacheKey, formattedResult);
      }

      return formattedResult;

    } catch (error) {
      this._handleError(error, 'Staff search failed', { params });
    }
  }

  async getStaffReport(staffId, params = {}) {
    try {
      const validatedStaffId = ParameterUtils.validateStaffId(staffId);
      const validation = StaffReportValidator.validateReportParams(params);
      
      if (!validation.isValid) {
        throw new ValidationError('Invalid report parameters', validation.errors);
      }

      const cleanParams = ParameterUtils.cleanParams(params);
      
      // FIXED: Use the exact endpoint from Laravel routes
      const response = await api.get(`/staff/reports/${validatedStaffId}`, { 
        params: cleanParams,
        timeout: CONFIG.DEFAULT_TIMEOUT
      });
      
      const result = handleApiResponse(response);
      return {
        ...result,
        data: StaffReportFormatter.formatReportData(result)
      };

    } catch (error) {
      this._handleError(error, 'Staff report generation failed', { staffId, params });
    }
  }

  async downloadStaffReport(staffId, params = {}) {
    try {
      const validatedStaffId = ParameterUtils.validateStaffId(staffId);
      const validation = StaffReportValidator.validateReportParams(params);
      
      if (!validation.isValid) {
        throw new ValidationError('Invalid download parameters', validation.errors);
      }

      const cleanParams = ParameterUtils.cleanParams(params);
      const format = cleanParams.format || 'pdf';
      const defaultFilename = `staff-report-${validatedStaffId}-${new Date().toISOString().split('T')[0]}.${format}`;

      // FIXED: Use the exact endpoint from Laravel routes
      const response = await api.get(`/staff/reports/${validatedStaffId}/download`, {
        params: cleanParams,
        responseType: 'blob',
        timeout: CONFIG.DEFAULT_TIMEOUT
      });
      
      return FileDownloadManager.handleFileDownload(response, defaultFilename);

    } catch (error) {
      this._handleError(error, 'Staff report download failed', { staffId, params });
    }
  }

  async getStaffList() {
    try {
      const cacheKey = 'staff-list';
      const cached = this._cache.get(cacheKey);
      if (cached) return cached;

      // FIXED: Use the exact endpoint from Laravel routes
      const response = await api.get('/staff/reports/', {
        timeout: CONFIG.DEFAULT_TIMEOUT
      });
      
      const result = handleApiResponse(response);
      const formattedResult = {
        ...result,
        data: {
          ...result.data,
          staff: StaffReportFormatter.formatStaffList(result)
        }
      };

      this._cache.set(cacheKey, formattedResult);
      return formattedResult;

    } catch (error) {
      this._handleError(error, 'Failed to fetch staff list');
    }
  }

  async exportSystemReport(params = {}) {
    try {
      const validation = StaffReportValidator.validateReportParams(params);
      
      if (!validation.isValid) {
        throw new ValidationError('Invalid export parameters', validation.errors);
      }

      const cleanParams = ParameterUtils.cleanParams(params);
      const format = cleanParams.format || 'pdf';
      const defaultFilename = `system-report-${new Date().toISOString().split('T')[0]}.${format}`;

      // FIXED: Use the exact endpoint from Laravel routes
      const response = await api.get('/staff/reports/system/export', {
        params: cleanParams,
        responseType: 'blob',
        timeout: CONFIG.DEFAULT_TIMEOUT
      });
      
      return FileDownloadManager.handleFileDownload(response, defaultFilename);

    } catch (error) {
      this._handleError(error, 'System report export failed', { params });
    }
  }

  async getReportSummary(params = {}) {
    try {
      const validation = StaffReportValidator.validateReportParams(params);
      
      if (!validation.isValid) {
        throw new ValidationError('Invalid summary parameters', validation.errors);
      }

      const cleanParams = ParameterUtils.cleanParams(params);
      
      // FIXED: Use the exact endpoint from Laravel routes
      const response = await api.get('/staff/reports/summary', { 
        params: cleanParams,
        timeout: CONFIG.DEFAULT_TIMEOUT
      });
      
      const result = handleApiResponse(response);
      return {
        ...result,
        data: StaffReportFormatter.formatSummaryData(result)
      };

    } catch (error) {
      this._handleError(error, 'Failed to fetch report summary', { params });
    }
  }

  // Utility methods
  getAvailableFormats() {
    return [
      { value: 'pdf', label: 'PDF Document', icon: '📄' },
      { value: 'csv', label: 'CSV Spreadsheet', icon: '📊' }
    ];
  }

  getStaffLevelOptions() {
    return [
      { value: '', label: 'All Levels' },
      { value: 1, label: 'Level 1 - Junior Staff' },
      { value: 2, label: 'Level 2 - Senior Staff' },
      { value: 3, label: 'Level 3 - Lead Staff' }
    ];
  }

  getDateRangePresets() {
    if (this._dateRangePresets) {
      return this._dateRangePresets;
    }
    
    this._dateRangePresets = this._calculateDateRangePresets();
    return this._dateRangePresets;
  }

  calculatePerformanceIndicators(performanceMetrics) {
    if (!performanceMetrics) return {};

    const indicators = {};

    // Response time indicator
    const avgResponse = performanceMetrics.response_metrics?.average_response_time_minutes || 0;
    indicators.responseTime = this._getPerformanceIndicator(avgResponse, [30, 60, 120], 'minutes');

    // SLA compliance indicator
    const slaRate = performanceMetrics.sla_performance?.sla_compliance_rate || 0;
    indicators.slaCompliance = this._getPerformanceIndicator(slaRate, [95.0, 85.0, 70.0], '%', true);

    // Completion rate indicator
    const completionRate = performanceMetrics.workload_metrics?.completion_rate || 0;
    indicators.completionRate = this._getPerformanceIndicator(completionRate, [90.0, 75.0, 60.0], '%', true);

    return indicators;
  }

  generateInsights(reportData) {
    if (!reportData) return [];

    const insights = [];
    const performance = reportData.performanceMetrics;
    const tickets = reportData.ticketStatistics;
    const actions = reportData.actionStatistics;
    const staff = reportData.staffInfo;

    // Expertise insights
    if (staff.expertiseSummary?.total_categories > 0) {
      insights.push({
        type: 'info',
        title: 'Expertise Profile',
        message: `Staff has expertise in ${staff.expertiseSummary.total_categories} categories, with ${staff.expertiseSummary.can_receive_forwarded} available for forwarded tickets.`
      });
    }

    // Response time insights
    const avgResponse = performance?.response_metrics?.average_response_time_minutes;
    if (avgResponse) {
      if (avgResponse <= 30.0) {
        insights.push({
          type: 'positive',
          title: 'Excellent Response Time',
          message: `Average response time of ${StaffReportFormatter.formatDuration(avgResponse)} is well within best practices.`
        });
      } else if (avgResponse > 120.0) {
        insights.push({
          type: 'warning',
          title: 'Slow Response Time',
          message: `Average response time of ${StaffReportFormatter.formatDuration(avgResponse)} exceeds recommended thresholds.`
        });
      }
    }

    // Completion rate insights
    const totalAssigned = tickets?.total_assigned || 0;
    const resolved = tickets?.by_status?.resolved || 0;
    const closed = tickets?.by_status?.closed || 0;
    const completed = resolved + closed;
    
    if (totalAssigned > 0) {
      const completionRate = (completed / totalAssigned) * 100;
      if (completionRate >= 80.0) {
        insights.push({
          type: 'positive',
          title: 'High Completion Rate',
          message: `Completed ${StaffReportFormatter.formatPercentage(completionRate)} of assigned tickets (${resolved} resolved, ${closed} closed).`
        });
      } else if (completionRate < 50.0) {
        insights.push({
          type: 'attention',
          title: 'Low Completion Rate',
          message: `Only ${StaffReportFormatter.formatPercentage(completionRate)} of tickets completed. Consider workload review.`
        });
      }
    }

    // Escalation insights
    const escalated = actions?.escalated || 0;
    if (totalAssigned > 0 && escalated > 0) {
      const escalationRate = (escalated / totalAssigned) * 100;
      if (escalationRate > 20.0) {
        insights.push({
          type: 'attention',
          title: 'High Escalation Rate',
          message: `${StaffReportFormatter.formatPercentage(escalationRate)} of tickets escalated. Consider additional training or workload adjustment.`
        });
      } else if (escalationRate < 5.0) {
        insights.push({
          type: 'positive',
          title: 'Low Escalation Rate',
          message: `Only ${StaffReportFormatter.formatPercentage(escalationRate)} of tickets escalated, showing good problem-solving capability.`
        });
      }
    }

    // SLA compliance insights
    const slaRate = performance?.sla_performance?.sla_compliance_rate || 0;
    if (slaRate >= 95.0) {
      insights.push({
        type: 'positive',
        title: 'Excellent SLA Compliance',
        message: `${StaffReportFormatter.formatPercentage(slaRate)} SLA compliance rate demonstrates consistent performance.`
      });
    } else if (slaRate < 70.0) {
      insights.push({
        type: 'warning',
        title: 'SLA Compliance Below Target',
        message: `${StaffReportFormatter.formatPercentage(slaRate)} SLA compliance may indicate workload or training needs.`
      });
    }

    return insights;
  }

  // Color utility methods
  getCompletionRateColor(rate) {
    if (rate >= 90.0) return 'text-green-600';
    if (rate >= 75.0) return 'text-blue-600';
    if (rate >= 60.0) return 'text-yellow-600';
    return 'text-red-600';
  }

  getEscalationRateColor(rate) {
    if (rate <= 5.0) return 'text-green-600';
    if (rate <= 10.0) return 'text-blue-600';
    if (rate <= 20.0) return 'text-yellow-600';
    return 'text-red-600';
  }

  // Cache management
  clearCache() {
    this._cache.clear();
    this._dateRangePresets = null;
  }

  // Private methods
  _calculateDateRangePresets() {
    const now = new Date();
    const formatDate = (date) => date.toISOString().split('T')[0];

    return [
      {
        label: 'Last 7 Days',
        dateFrom: formatDate(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)),
        dateTo: formatDate(now)
      },
      {
        label: 'Last 30 Days',
        dateFrom: formatDate(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)),
        dateTo: formatDate(now)
      },
      {
        label: 'Last 90 Days',
        dateFrom: formatDate(new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)),
        dateTo: formatDate(now)
      },
      {
        label: 'This Month',
        dateFrom: formatDate(new Date(now.getFullYear(), now.getMonth(), 1)),
        dateTo: formatDate(now)
      },
      {
        label: 'Last Month',
        dateFrom: formatDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        dateTo: formatDate(new Date(now.getFullYear(), now.getMonth(), 0))
      },
      {
        label: 'This Quarter',
        dateFrom: formatDate(new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1)),
        dateTo: formatDate(now)
      },
      {
        label: 'Last Quarter',
        dateFrom: formatDate(new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 - 3, 1)),
        dateTo: formatDate(new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 0))
      }
    ];
  }

  _getPerformanceIndicator(value, thresholds, unit, higherIsBetter = false) {
    const levels = ['excellent', 'good', 'average', 'poor'];
    const colors = ['green', 'blue', 'yellow', 'red'];
    const labels = ['Excellent', 'Good', 'Average', 'Needs Improvement'];

    let level = 3; // Default to poor
    
    if (higherIsBetter) {
      for (let i = 0; i < thresholds.length; i++) {
        if (value >= thresholds[i]) {
          level = i;
          break;
        }
      }
    } else {
      for (let i = 0; i < thresholds.length; i++) {
        if (value <= thresholds[i]) {
          level = i;
          break;
        }
      }
    }

    return {
      status: levels[level],
      color: colors[level],
      label: labels[level],
      value: value,
      unit: unit
    };
  }

  _handleError(error, message, context = {}) {
    const errorMessage = handleApiError(error);
    
    // Log error with context
    console.error(message, {
      context,
      error: error.message || error,
      stack: error.stack
    });
    
    // Handle specific error types
    if (error instanceof ValidationError) {
      throw error;
    }
    
    if (error.response?.status === 403) {
      throw new StaffReportError('Permission denied for staff reports', 'PERMISSION_DENIED', context);
    }
    
    if (error.response?.status === 404) {
      throw new StaffReportError('Resource not found', 'NOT_FOUND', context);
    }
    
    if (error.response?.status === 422) {
      throw new StaffReportError(`Validation error: ${errorMessage}`, 'VALIDATION_ERROR', context);
    }
    
    if (error.response?.status >= 500) {
      throw new StaffReportError('Server error occurred', 'SERVER_ERROR', context);
    }
    
    // Generic error
    throw new StaffReportError(`${message}: ${errorMessage}`, 'UNKNOWN_ERROR', context);
  }
}

// Export singleton instance
export default new StaffReportService();

// Export classes for testing or advanced usage
export {
  StaffReportService,
  StaffReportError,
  ValidationError,
  StaffReportFormatter,
  StaffReportValidator
};